import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Plus, Trash2, ListChecks, Check, Pencil, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function CustomLists() {
  const [lists, setLists] = useState([]);
  const [activeList, setActiveList] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newItemName, setNewItemName] = useState("");
  const [newItemAmount, setNewItemAmount] = useState("");
  const [newItemUnit, setNewItemUnit] = useState("");
  const [editingName, setEditingName] = useState(null);
  const [editNameValue, setEditNameValue] = useState("");
  const [sortAlpha, setSortAlpha] = useState(false);

  const loadLists = useCallback(async () => {
    try {
      const res = await api.getCustomLists();
      setLists(res.data);
      if (activeList) {
        const updated = res.data.find((l) => l.id === activeList.id);
        if (updated) setActiveList(updated);
      }
    } catch { toast.error("Kunne ikke laste lister"); }
  }, [activeList]);

  useEffect(() => { loadLists(); }, [loadLists]);

  const handleCreate = async () => {
    if (!newListName.trim()) return;
    try {
      const res = await api.createCustomList({ name: newListName.trim() });
      setLists((p) => [...p, res.data]);
      setActiveList(res.data);
      setCreateOpen(false);
      setNewListName("");
      toast.success("Liste opprettet!");
    } catch { toast.error("Feil ved oppretting"); }
  };

  const handleDeleteList = async (listId) => {
    try {
      await api.deleteCustomList(listId);
      setLists((p) => p.filter((l) => l.id !== listId));
      if (activeList?.id === listId) setActiveList(null);
      toast.success("Liste slettet");
    } catch { toast.error("Feil ved sletting"); }
  };

  const handleRenameList = async (listId) => {
    if (!editNameValue.trim()) return;
    try {
      await api.updateCustomList(listId, { name: editNameValue.trim() });
      setLists((p) => p.map((l) => l.id === listId ? { ...l, name: editNameValue.trim() } : l));
      if (activeList?.id === listId) setActiveList((p) => ({ ...p, name: editNameValue.trim() }));
      setEditingName(null);
      toast.success("Navn oppdatert");
    } catch { toast.error("Feil"); }
  };

  const addItem = async () => {
    if (!newItemName.trim() || !activeList) return;
    const newItem = { id: crypto.randomUUID(), name: newItemName.trim(), amount: newItemAmount.trim(), unit: newItemUnit.trim(), checked: false };
    const updatedItems = [...(activeList.items || []), newItem];
    setActiveList((p) => ({ ...p, items: updatedItems }));
    setNewItemName(""); setNewItemAmount(""); setNewItemUnit("");
    try { await api.updateCustomList(activeList.id, { items: updatedItems }); }
    catch { toast.error("Feil"); }
  };

  const removeItem = async (itemId) => {
    if (!activeList) return;
    const updatedItems = activeList.items.filter((i) => i.id !== itemId);
    setActiveList((p) => ({ ...p, items: updatedItems }));
    try { await api.updateCustomList(activeList.id, { items: updatedItems }); }
    catch { toast.error("Feil"); }
  };

  const toggleItem = async (itemId) => {
    if (!activeList) return;
    const updatedItems = activeList.items.map((i) => i.id === itemId ? { ...i, checked: !i.checked } : i);
    setActiveList((p) => ({ ...p, items: updatedItems }));
    try { await api.updateCustomList(activeList.id, { items: updatedItems }); }
    catch { toast.error("Feil"); }
  };

  const checkedCount = activeList?.items?.filter((i) => i.checked).length || 0;
  const totalCount = activeList?.items?.length || 0;

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="custom-lists-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Mine Lister</h1>
          <p className="text-sm text-stone-500 mt-1">Lag egne handlelister etter behov</p>
        </div>
        <button onClick={() => setCreateOpen(true)} className="inline-flex items-center justify-center rounded-md bg-navy-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors gap-2" data-testid="create-list-btn">
          <Plus className="h-4 w-4" /> Ny liste
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Lists sidebar */}
        <div className="space-y-2" data-testid="lists-sidebar">
          {lists.length === 0 ? (
            <div className="text-center py-12 bg-white border border-stone-100 rounded-xl">
              <ListChecks className="h-10 w-10 mx-auto text-stone-300 mb-3" />
              <p className="text-sm text-stone-400">Ingen lister ennå</p>
            </div>
          ) : lists.map((list) => (
            <div
              key={list.id}
              className={`group flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                activeList?.id === list.id ? "bg-sailcloth-100 border-navy-100" : "bg-white border-stone-100 hover:border-stone-200"
              }`}
              onClick={() => setActiveList(list)}
              data-testid={`list-item-${list.id}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                activeList?.id === list.id ? "bg-navy-50 text-navy-800" : "bg-stone-100 text-stone-400"
              }`}>
                <ListChecks className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                {editingName === list.id ? (
                  <input value={editNameValue} onChange={(e) => setEditNameValue(e.target.value)} onBlur={() => handleRenameList(list.id)} onKeyDown={(e) => e.key === "Enter" && handleRenameList(list.id)} className="text-sm font-medium bg-transparent border-b border-seawater-500 outline-none w-full" autoFocus data-testid={`rename-input-${list.id}`} />
                ) : (
                  <span className="text-sm font-medium text-stone-700 block truncate">{list.name}</span>
                )}
                <span className="text-xs text-stone-400">{(list.items || []).length} varer</span>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => { setEditingName(list.id); setEditNameValue(list.name); }} className="p-1 hover:bg-stone-100 rounded text-stone-400 hover:text-stone-600" data-testid={`rename-list-${list.id}`}>
                  <Pencil className="h-3 w-3" />
                </button>
                <button onClick={() => handleDeleteList(list.id)} className="p-1 hover:bg-red-50 rounded text-stone-400 hover:text-red-500" data-testid={`delete-list-${list.id}`}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
              <ChevronRight className={`h-4 w-4 flex-shrink-0 ${activeList?.id === list.id ? "text-seawater-500" : "text-stone-300"}`} />
            </div>
          ))}
        </div>

        {/* Active list content */}
        <div className="md:col-span-2">
          {!activeList ? (
            <div className="text-center py-20 bg-white border border-stone-100 rounded-xl" data-testid="no-list-selected">
              <ListChecks className="h-12 w-12 mx-auto text-stone-300 mb-4" />
              <h3 className="text-lg font-medium text-stone-600" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Velg en liste</h3>
              <p className="text-sm text-stone-400 mt-1">Velg en liste fra sidepanelet eller opprett en ny</p>
            </div>
          ) : (
            <div className="bg-white border border-stone-100 rounded-xl" data-testid="active-list-content">
              {/* List header */}
              <div className="p-4 border-b border-stone-100">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h2 className="text-xl font-semibold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{activeList.name}</h2>
                  {totalCount > 1 && (
                    <button
                      onClick={() => setSortAlpha(!sortAlpha)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                        sortAlpha
                          ? "bg-navy-800 text-white border-navy-800"
                          : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"
                      }`}
                      data-testid="custom-sort-alpha"
                      title="Sorter alfabetisk A-Å"
                    >
                      A-Å
                    </button>
                  )}
                </div>
                {totalCount > 0 && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs text-stone-400 mb-1">
                      <span>{checkedCount} av {totalCount} avhuket</span>
                      <span>{Math.round((checkedCount / totalCount) * 100)}%</span>
                    </div>
                    <div className="w-full bg-stone-100 rounded-full h-1.5">
                      <div className="bg-seawater-600 h-1.5 rounded-full transition-all" style={{ width: `${(checkedCount / totalCount) * 100}%` }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Items */}
              {totalCount === 0 ? (
                <div className="p-8 text-center text-stone-400 text-sm">Ingen varer ennå. Legg til nedenfor.</div>
              ) : (
                <div className="divide-y divide-stone-50" data-testid="list-items">
                  {(sortAlpha
                    ? [...activeList.items].sort((a, b) => (a.name || "").localeCompare(b.name || "", "nb", { sensitivity: "base" }))
                    : activeList.items
                  ).map((item) => (
                    <div key={item.id} className={`flex items-center gap-3 px-4 py-3 group transition-all ${item.checked ? "opacity-50" : ""}`} data-testid={`custom-item-${item.id}`}>
                      <button onClick={() => toggleItem(item.id)} className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${item.checked ? "bg-seawater-600 border-seawater-600" : "border-stone-300 hover:border-seawater-500"}`} data-testid={`toggle-item-${item.id}`}>
                        {item.checked && <Check className="h-3 w-3 text-white" />}
                      </button>
                      <div className={`flex-1 min-w-0 ${item.checked ? "line-through text-stone-400" : ""}`}>
                        <span className="text-sm font-medium text-stone-700">{item.name}</span>
                        {(item.amount || item.unit) && <span className="text-xs text-stone-400 ml-2">{item.amount} {item.unit}</span>}
                      </div>
                      <button onClick={() => removeItem(item.id)} className="p-1.5 hover:bg-red-50 rounded-md text-stone-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100" data-testid={`remove-custom-item-${item.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add item form */}
              <div className="p-4 border-t border-stone-100 bg-stone-50/50 rounded-b-xl">
                <div className="flex gap-2">
                  <input value={newItemName} onChange={(e) => setNewItemName(e.target.value)} placeholder="Varenavn..." className="flex-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="custom-item-name" onKeyDown={(e) => e.key === "Enter" && addItem()} />
                  <input value={newItemAmount} onChange={(e) => setNewItemAmount(e.target.value)} placeholder="Mengde" className="w-20 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="custom-item-amount" />
                  <input value={newItemUnit} onChange={(e) => setNewItemUnit(e.target.value)} placeholder="Enhet" className="w-20 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="custom-item-unit" />
                  <button onClick={addItem} className="inline-flex items-center justify-center rounded-md bg-navy-800 px-4 py-2 text-sm font-medium text-white hover:bg-navy-900 transition-colors gap-1" data-testid="add-custom-item-btn">
                    <Plus className="h-4 w-4" /> Legg til
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create list dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-sm" data-testid="create-list-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ny liste</DialogTitle>
            <DialogDescription>Gi listen et navn</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <input value={newListName} onChange={(e) => setNewListName(e.target.value)} placeholder="F.eks. Storhandel, Kiosk, Baketur..." className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="list-name-input" onKeyDown={(e) => e.key === "Enter" && handleCreate()} />
            <button onClick={handleCreate} className="w-full inline-flex items-center justify-center rounded-md bg-navy-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors" data-testid="save-list-btn">Opprett</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
